import {
  MANIFEST_STORE_NAME,
  openSurfClipsAIDb,
} from "@/lib/storage/indexed-db";
import { loadSessionToken } from "@/lib/storage/session-record-store";

export type UploadFileStatus = "pending" | "uploading" | "uploaded" | "failed";

export type CompletedPartRecord = {
  partNumber: number;
  etag: string;
};

export type UploadManifestFile = {
  clientFileKey: string;
  serverFileId: string;
  name: string;
  sizeBytes: number;
  contentType: string;
  partSize: number;
  multipart: boolean;
  uploadId: string | null;
  completedParts: CompletedPartRecord[];
  status: UploadFileStatus;
  error?: string;
};

/**
 * In-memory upload working set. `sessionToken` is hydrated from the session
 * record (the single persisted home for the token) and is never written back
 * into the manifest store.
 */
export type UploadManifest = {
  clientSessionId: string;
  sessionToken: string;
  createdAt: number;
  files: UploadManifestFile[];
};

type StoredUploadManifest = {
  clientSessionId: string;
  createdAt: number;
  files: UploadManifestFile[];
};

function toStoredManifest(manifest: UploadManifest): StoredUploadManifest {
  return {
    clientSessionId: manifest.clientSessionId,
    createdAt: manifest.createdAt,
    files: manifest.files,
  };
}

export async function saveUploadManifest(manifest: UploadManifest) {
  const db = await openSurfClipsAIDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(MANIFEST_STORE_NAME, "readwrite");
      tx.objectStore(MANIFEST_STORE_NAME).put(toStoredManifest(manifest));
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(tx.error ?? new Error("Failed to save upload manifest"));
    });
  } finally {
    db.close();
  }
}

async function readStoredManifest(clientSessionId: string) {
  const db = await openSurfClipsAIDb();
  try {
    return await new Promise<StoredUploadManifest | null>((resolve, reject) => {
      const tx = db.transaction(MANIFEST_STORE_NAME, "readonly");
      const request = tx.objectStore(MANIFEST_STORE_NAME).get(clientSessionId);
      request.onerror = () =>
        reject(request.error ?? new Error("Failed to load upload manifest"));
      request.onsuccess = () =>
        resolve((request.result as StoredUploadManifest | undefined) ?? null);
    });
  } finally {
    db.close();
  }
}

export async function loadUploadManifest(clientSessionId: string) {
  const stored = await readStoredManifest(clientSessionId);
  if (!stored) return null;

  const sessionToken = (await loadSessionToken(clientSessionId)) ?? "";
  return {
    clientSessionId: stored.clientSessionId,
    sessionToken,
    createdAt: stored.createdAt,
    files: stored.files ?? [],
  } satisfies UploadManifest;
}

export async function listIncompleteUploadManifests() {
  const db = await openSurfClipsAIDb();
  try {
    const stored = await new Promise<StoredUploadManifest[]>((resolve, reject) => {
      const tx = db.transaction(MANIFEST_STORE_NAME, "readonly");
      const request = tx.objectStore(MANIFEST_STORE_NAME).getAll();
      request.onerror = () =>
        reject(request.error ?? new Error("Failed to list upload manifests"));
      request.onsuccess = () => resolve(request.result as StoredUploadManifest[]);
    });

    // The token is resolved per-session via loadUploadManifest when a run
    // starts, so the list itself carries no token.
    return stored
      .filter((manifest) => manifest.files.some((file) => file.status !== "uploaded"))
      .map((manifest) => ({ ...manifest, sessionToken: "" }) satisfies UploadManifest);
  } finally {
    db.close();
  }
}

export async function deleteUploadManifest(clientSessionId: string) {
  const db = await openSurfClipsAIDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(MANIFEST_STORE_NAME, "readwrite");
      tx.objectStore(MANIFEST_STORE_NAME).delete(clientSessionId);
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(tx.error ?? new Error("Failed to delete upload manifest"));
    });
  } finally {
    db.close();
  }
}
